"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  ClauseSteps,
  chainText,
  type PartsContext,
  SampleCheck,
} from "@/components/tools/algebra/inequality-parts";
import { NumberLine } from "@/components/tools/algebra/number-line";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Clause,
  type Connector,
  solveInequalityText,
} from "@/lib/tool-kit/algebra/inequality";
import {
  conditionText,
  formatIntervalSet,
  type IntervalSet,
  isAllReals,
  isEmptySet,
} from "@/lib/tool-kit/algebra/intervals";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import type { NumberStyle } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const INEQUALITY_SOLVER_STRINGS = {
  inequality: "Inequality",
  inequalityHint:
    "Use <, >, ≤ or ≥ (or <= and >=). Chain two, as in −3 < 2x + 1 ≤ 7, or join two with “and” / “or”.",
  examplesLabel: "Try an example",
  exampleLabels: [
    "Negative coefficient",
    "Double",
    "And",
    "Or",
    "All reals",
    "No solution",
  ],
  examples: [
    "−2x + 3 ≤ 11",
    "−3 < 2x + 1 ≤ 7",
    "2x − 1 ≥ 3 and x + 4 < 10",
    "x + 3 > 5 or 2x < −4",
    "2x + 1 < 2x + 5",
    "x + 1 > x + 3",
  ],
  results: "Solution",
  solution: "Inequality form",
  interval: "Interval notation",
  setBuilder: "Set-builder notation",
  allReals: "All real numbers",
  noSolution: "No solution",
  copy: "Copy solution",
  copied: "Copied!",
  numberLineHeading: "Number line",
  numberLineTitle:
    "Number line with the solution set {interval} shaded; a filled circle is included and an open circle is excluded",
  numberLineKey:
    "Filled circle: the end value is included. Open circle: it is excluded. An arrow means the set continues without end.",
  stepsHeading: "Step by step",
  clauseHeading: "Inequality {n}",
  start: "Start with the inequality.",
  simplify: "Simplify: expand the brackets and combine like terms.",
  bothSides: "both sides",
  allParts: "all three parts",
  clear:
    "Multiply {every} by {factor} to clear the fractions. The number is positive, so the signs stay.",
  add: "Add {term} to {every}.",
  subtract: "Subtract {term} from {every}.",
  divide:
    "Divide {every} by {divisor}. The number is positive, so the signs stay.",
  divideNegative:
    "Divide {every} by {divisor}. The number is negative, so every inequality sign reverses.",
  flipNote: "Sign reversed: {from} becomes {to}.",
  reverse:
    "Write the parts in increasing order, so the smaller number is on the left.",
  combineHeading: "Combining the inequalities",
  and: "“And”: both must hold, so keep only the overlap (the intersection).",
  or: "“Or”: either may hold, so combine the solutions (the union).",
  combined: "Result: {set}",
  everyReal:
    "The variable cancels and what is left, {statement}, is always true, so every real number is a solution.",
  noReal:
    "The variable cancels and what is left, {statement}, is false, so no real number is a solution.",
  checkHeading: "Check with a test value",
  checkIntro: "Substitute {variable} = {value}, a number in the solution set:",
  true: "true",
  false: "false",
  point: "{variable} = {value}",
  enter: "Enter an inequality to solve it.",
  errors: {
    empty: "Enter an inequality to solve it.",
    "too-long": "That inequality is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not allowed. Use digits, one letter, + − × / ^ and brackets.",
    unexpected: "Something is out of place near “{token}”.",
    end: "The inequality ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match.",
    "divide-by-variable":
      "Dividing by a variable is not linear. Divide only by numbers, such as x/3.",
    "divide-by-zero": "Division by zero is not allowed.",
    exponent: "Powers must be whole numbers, such as x^2.",
    "too-large": "That expression is too large to expand.",
    "no-relation": "“{token}” needs a relation: <, >, ≤ or ≥.",
    "no-inequality":
      "“{token}” uses an equals sign. For an equation, use the Linear Equation Solver.",
    "many-relations": "Use at most two relation signs in one clause.",
    "too-many-relations": "Use at most two relation signs in one clause.",
    "too-many-clauses": "Join at most {token} inequalities.",
    "many-variables":
      "More than one letter appears ({token}). This tool solves for one variable.",
    "missing-side": "Both sides of a relation sign need something on them.",
    nonlinear:
      "The highest power of {token} is above 1, so this is not a linear inequality. Quadratic and higher inequalities are not solved here.",
  },
} as const;

type Strings = typeof INEQUALITY_SOLVER_STRINGS;

const DECIMAL_INPUT = /[.,]/;

function answerText(
  set: IntervalSet,
  variable: string,
  style: NumberStyle,
  mark: string,
  strings: Strings
): string {
  if (isEmptySet(set)) {
    return strings.noSolution;
  }
  if (isAllReals(set)) {
    return strings.allReals;
  }
  return set
    .map((interval) => conditionText(interval, variable, style, mark))
    .join(" or ");
}

function CombineNote({
  clauses,
  joins,
  set,
  style,
  mark,
  strings,
}: {
  clauses: Clause[];
  joins: Connector[];
  set: IntervalSet;
  style: NumberStyle;
  mark: string;
  strings: Strings;
}) {
  if (clauses.length < 2) {
    return null;
  }
  return (
    <div className={`${RESULT_CARD} space-y-1 text-sm`}>
      <h3 className={WIDGET_H3}>{strings.combineHeading}</h3>
      {[...new Set(joins)].map((join) => (
        <p className="text-muted-foreground" key={join}>
          {join === "and" ? strings.and : strings.or}
        </p>
      ))}
      <p className="font-mono text-foreground">
        {t(strings.combined, { set: formatIntervalSet(set, style, mark) })}
      </p>
    </div>
  );
}

function lastStatement(clause: Clause, ctx: PartsContext): string {
  const last = clause.solution.steps.at(-1);
  return last ? chainText(last.parts, last.rels, ctx) : "";
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const reading = solveInequalityText(text);
  if (!reading.ok) {
    const template = strings.errors[reading.error.code] ?? strings.errors.empty;
    return (
      <ErrorNotice>{t(template, { token: reading.error.token })}</ErrorNotice>
    );
  }
  const style: NumberStyle = DECIMAL_INPUT.test(text) ? "decimal" : "fraction";
  const { variable, clauses, joins, set } = reading;
  const ctx: PartsContext = { variable, strings, style, mark };
  const interval = formatIntervalSet(set, style, mark);
  const answer = answerText(set, variable, style, mark, strings);
  const builder = isEmptySet(set)
    ? "∅"
    : `{${variable} | ${set.map((i) => conditionText(i, variable, style, mark)).join(" or ")}}`;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.solution} value={answer} />
        <StatCard label={strings.interval} value={interval} />
        <StatCard label={strings.setBuilder} value={builder} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={`${answer}; ${interval}`}
      />
      <h3 className={WIDGET_H3}>{strings.numberLineHeading}</h3>
      <NumberLine
        set={set}
        style={style}
        title={t(strings.numberLineTitle, { interval })}
      />
      <p className="text-muted-foreground text-xs">{strings.numberLineKey}</p>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      {clauses.map((clause, index) => (
        <div className="space-y-2" key={`${index}-${clause.source ?? ""}`}>
          {clauses.length > 1 ? (
            <p className="font-medium text-foreground text-sm">
              {t(strings.clauseHeading, { n: index + 1 })}
            </p>
          ) : null}
          <ClauseSteps clause={clause} ctx={ctx} />
          {clause.solution.outcome.kind === "identity" ? (
            <Notice>
              {t(strings.everyReal, { statement: lastStatement(clause, ctx) })}
            </Notice>
          ) : null}
          {clause.solution.outcome.kind === "contradiction" ? (
            <Notice>
              {t(strings.noReal, { statement: lastStatement(clause, ctx) })}
            </Notice>
          ) : null}
        </div>
      ))}
      <CombineNote
        clauses={clauses}
        joins={joins}
        mark={mark}
        set={set}
        strings={strings}
        style={style}
      />
      {clauses.length === 1 && clauses[0].solution.outcome.kind === "solved" ? (
        <>
          <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
          <SampleCheck clause={clauses[0]} ctx={ctx} set={set} />
        </>
      ) : null}
    </>
  );
}

export default function InequalitySolverWidget() {
  const strings = useToolUi(INEQUALITY_SOLVER_STRINGS);
  const [text, setText] = useState<string>(strings.examples[0]);
  return (
    <>
      <TextField
        hint={strings.inequalityHint}
        id="ineq-input"
        label={strings.inequality}
        mono
        onChange={setText}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.examples.map((example, i) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {strings.exampleLabels[i]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {text.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results strings={strings} text={text} />
        )}
      </section>
    </>
  );
}
